import { initAdv, injectAdv, shouldInjectAdv, shouldRemoveAdv, destroyAdv, refreshAdv } from '@sky-uk/sky-adv';

import { isLocalDomain } from '../../utils/aem-utils';
import { filter } from '../../utils/array-utils';
import { allowBodyScroll, preventBodyScroll } from '../../utils/body-scroll';
import { getLocation, getParamFromUrl } from '../../utils/build-urls';
import { changeUrlNoHistory } from '../../utils/change-url';
import { isMobile } from '../../utils/device-utils';
import { hideElement, isNodeOnFocus, scrollToElement } from '../../utils/dom-utils';
import { trackComscore } from '../../utils/analytics';
import { satelliteTracker } from '../../services/tracking/satellite-tracking-service';
import { dynamicTrackingOnNewsUCanUse } from '../../services/tracking/parsely-tracking-service';
import logger from '../../services/logging/logging-service';
import { stickyHeroPlayerState } from '../sticky-hero-player/state';

/** *******
 * CONSTANTS **
 *********/
const DISABLED_CLASSNAME = 'is-disabled';
const CARD_NAVIGATOR_HIDDEN_CLASSNAME = 'c-card-navigator--hidden';
const CARD_NAVIGATOR_OPEN_CLASSNAME = 'c-card-navigator--open';
const CARD_OPEN_CLASSNAME = 'c-extendable-card--open';
const CARD_TRUNCATED_CLASSNAME = 'c-extendable-card--truncated';
const PLAYER_WITH_NAVIGATOR_CLASSNAME = 'c-player-card--with-card-navigator';
const ADV_STICKY_CONTAINER_SELECTOR = '#advStickyContainer';
const TOP_THRESHOLD = 400;
export const RESPONSIVE_OFFSET = {
  mobile: 70,
  desktop: 135,
};
const BASE_LOCALHOST = 'https://localhost.sky.it:4200';
const QUERY_PARAM_NAME = 'card';
const SMOOTH_SCROLL_DELAY = 800;
const HERO_PLAYER_TOP_IN = 65;
const HERO_PLAYER_TOP_OUT = -250;
const TRACKED_BY_COMSCORE = 'tracked';
const GALLERY_TRACKING_EVENT = 'change_gallery';
export const READ_MORE_CARD_ID = 'last';
const DATA_ANALYTICS_ELEMENT = 'data-analytics-element';
const ANALYTICS_TRACKING_ON_READ_MORE_CARD = 'leggi-anche';
const INTRO_ADV_CARD_THRESHOLD = 2;
const OUT_OF_EDGE_INDEX = -1;
let currentCardIndex = OUT_OF_EDGE_INDEX;

const $advTopDataNode = document.querySelector('#dataAdvTop');
const $advStickyDataNode = document.querySelector('#dataAdvSticky');

/** *******
 * UTILS **
 *********/
const shouldShowAdvIntro = (() => {
  let hasShown = false;
  return (cardIndex) => {
    if (!hasShown && cardIndex === INTRO_ADV_CARD_THRESHOLD) {
      hasShown = true;
      return true;
    }
    return false;
  };
})();
const currentLocation = isLocalDomain() ? BASE_LOCALHOST : getLocation().withPathName;
const isFirstCard = (index) => index === 0;
const isLastCard = ($cards, index) => index === $cards.length - 1;
const isCardNavigatorOpen = ($cardNavigator) => $cardNavigator.className.includes(CARD_NAVIGATOR_OPEN_CLASSNAME);
const isCardOpen = ($card) => $card.className.includes(CARD_OPEN_CLASSNAME);
const isEdgeCard = ($cards, cardIndex) => cardIndex === 0 || cardIndex === $cards.length - 1;
const openCard = ($card) => {
  $card.classList.add(CARD_OPEN_CLASSNAME);
  $card.classList.remove(CARD_TRUNCATED_CLASSNAME);
};
const openAllCards = ($cards) => {
  $cards.forEach(($card) => {
    openCard($card);
  });
};
const updateUrl = (cardId) => {
  if (cardId) {
    changeUrlNoHistory(`${currentLocation}?${QUERY_PARAM_NAME}=${cardId}`);
    return;
  }
  changeUrlNoHistory(currentLocation);
};
const toggleMediaWrapper = ({ $heroMediaWrapper, $playerHero, delay = 0, show }) => {
  const playerTopOnMobile = show ? HERO_PLAYER_TOP_IN : HERO_PLAYER_TOP_OUT;

  setTimeout(() => {
    if (isMobile()) {
      $heroMediaWrapper.style.top = `${playerTopOnMobile}px`;
    } else {
      $playerHero.classList.toggle(PLAYER_WITH_NAVIGATOR_CLASSNAME);
    }
  }, delay);
};
const showCardNavigator = ($cardNavigator) => {
  $cardNavigator?.classList.remove(CARD_NAVIGATOR_HIDDEN_CLASSNAME);
};
const hideCardNavigator = ($cardNavigator, $overlay) => {
  $cardNavigator?.classList.add(CARD_NAVIGATOR_HIDDEN_CLASSNAME);
  hideCardNavigatorLinks($cardNavigator, $overlay);
};
const toggleOverlay = ($overlay, show) =>
  show ? $overlay.classList.remove('is-hidden') : $overlay.classList.add('is-hidden');
const showCardNavigatorLinks = ($cardNavigator, $overlay) => {
  preventBodyScroll();
  $cardNavigator?.classList.add(CARD_NAVIGATOR_OPEN_CLASSNAME);
  toggleOverlay($overlay, true);
};
const hideCardNavigatorLinks = ($cardNavigator, $overlay) => {
  allowBodyScroll();
  $cardNavigator?.classList.remove(CARD_NAVIGATOR_OPEN_CLASSNAME);
  toggleOverlay($overlay, false);
};
const setActiveLink = ($cardNavigatorLinks) => {
  $cardNavigatorLinks.forEach(($link, index) => {
    if (index === currentCardIndex) {
      $link.dataset.state = 'active';
    } else {
      $link.dataset.state = '';
    }
  });
};
const disableButton = ($button) => $button.classList.add(DISABLED_CLASSNAME);
const enableButton = ($button) => $button.classList.remove(DISABLED_CLASSNAME);
const updateCurrentCardIndex = (index) => {
  currentCardIndex = index;
};
const updateCounter = ({ $counter, index, cardsLength }) => {
  updateCurrentCardIndex(index);
  $counter.innerHTML = `${currentCardIndex + 1}/${cardsLength}`;
};
const getCardIdFromUrl = () => getParamFromUrl(QUERY_PARAM_NAME);
const trackAnalytics = ({ $card, cardId, $readMoreCard }) => {
  const isCardTrackedByComscore = $card.dataset.state === TRACKED_BY_COMSCORE;
  if (!isCardTrackedByComscore) {
    trackComscore();
    $card.dataset.state = TRACKED_BY_COMSCORE;
  }
  if ($readMoreCard && cardId === READ_MORE_CARD_ID) {
    $readMoreCard.setAttribute(DATA_ANALYTICS_ELEMENT, ANALYTICS_TRACKING_ON_READ_MORE_CARD);
  }
  if ($readMoreCard && cardId !== READ_MORE_CARD_ID) {
    $readMoreCard.removeAttribute(DATA_ANALYTICS_ELEMENT);
  }
  satelliteTracker.trackInWeb(GALLERY_TRACKING_EVENT, { hash: cardId });
  dynamicTrackingOnNewsUCanUse(cardId);
};

/**
 * ON MOBILE --> at the time of injection it shows the sticky banner (above the card navigation on news u can use component).
 * ON DESKTOP --> at the time of injection it shows the FULLPAGE format
 * @param {*} $advStickyDataNode
 */
const injectAdvSticky = ($advStickyDataNode) => {
  logger.info('[ADV STICKY] init adv');
  const $advContainer = document.querySelector(ADV_STICKY_CONTAINER_SELECTOR);
  injectAdv($advStickyDataNode, $advContainer);
};

/** *********
 * HANDLERS *
 ************/
const handleHideReadMoreContent = ($readMoreCard) => {
  const $inlineCards = $readMoreCard.querySelectorAll('.c-inline-card');
  $inlineCards.forEach(($inlineCard) => {
    const locationWithPathname = getLocation().withPathName;
    const href = $inlineCard.getAttribute('href');
    if (locationWithPathname === href) {
      hideElement($inlineCard);
    }
  });
};

const handleComponentOnFocus = (() => {
  let isComponentOnFocus = false;

  return ({ $newsYouCanUse, $cardNavigator, $overlay, $heroMediaWrapper, $playerHero, isLastCard }) => {
    if (isMobile() && shouldRemoveAdv($advStickyDataNode) && !isNodeOnFocus($newsYouCanUse, TOP_THRESHOLD)) {
      destroyAdv($advStickyDataNode);
    }
    if (!isComponentOnFocus && isNodeOnFocus($newsYouCanUse, TOP_THRESHOLD)) {
      showCardNavigator($cardNavigator);
      if (!stickyHeroPlayerState.getState('isVideoFinished') && isMobile()) {
        stickyHeroPlayerState.setState('shouldNotSticky', true);
      }
      toggleMediaWrapper({ $heroMediaWrapper, $playerHero, show: false });
      isComponentOnFocus = true;
    }
    if (isComponentOnFocus && !isNodeOnFocus($newsYouCanUse, TOP_THRESHOLD)) {
      toggleMediaWrapper({ $heroMediaWrapper, $playerHero, show: true });
      updateCurrentCardIndex(OUT_OF_EDGE_INDEX);
      updateUrl();
      if (isMobile() || !isLastCard) {
        hideCardNavigator($cardNavigator, $overlay);
        if (!stickyHeroPlayerState.getState('isVideoFinished')) {
          stickyHeroPlayerState.setState('shouldNotSticky', false);
        }
      }
      isComponentOnFocus = false;
    }
  };
})();

const handleScrolling = ({
  $newsYouCanUse,
  $cards,
  $counter,
  $cardNavigator,
  $cardNavigatorUpBtn,
  $cardNavigatorDownBtn,
  $cardNavigatorLinks,
  $overlay,
  $heroMediaWrapper,
  $playerHero,
  $readMoreCard,
}) => {
  // Check possible new card on focus
  for (let i = 0; i < $cards.length; i++) {
    const $card = $cards[i];
    if (isNodeOnFocus($card, TOP_THRESHOLD) && currentCardIndex !== i) {
      // A NEW card is on focus!
      if (!isCardOpen($card) && isMobile()) {
        openCard($card);
      }
      if (!isCardOpen($card) && !isMobile()) {
        openAllCards($cards);
      }
      trackAnalytics({ $card, cardId: $card.id, $readMoreCard });
      updateUrl($card.id);
      updateCounter({ $counter, index: i, cardsLength: $cards.length });
      setActiveLink($cardNavigatorLinks);
      if (isMobile() && shouldInjectAdv($advStickyDataNode) && isNodeOnFocus($newsYouCanUse, TOP_THRESHOLD)) {
        injectAdvSticky($advStickyDataNode);
        initAdv($advStickyDataNode);
      } else if (isMobile()) {
        refreshAdv($advStickyDataNode, shouldShowAdvIntro(currentCardIndex));
      }
      if (!isMobile() && shouldInjectAdv($advStickyDataNode) && shouldShowAdvIntro(currentCardIndex)) {
        // NOTE: only on desktop, at the time of injection the adv is shown on fullscreen format, so we do not need to refresh it
        injectAdvSticky($advStickyDataNode);
        initAdv($advStickyDataNode);
      }
      if (!isMobile()) {
        refreshAdv($advTopDataNode, shouldShowAdvIntro(currentCardIndex));
      }
      break;
    }
  }

  // The whole component is/is not on focus
  if (isEdgeCard($cards, currentCardIndex)) {
    handleComponentOnFocus({
      $newsYouCanUse,
      $cardNavigator,
      $overlay,
      $heroMediaWrapper,
      $playerHero,
      isLastCard: isLastCard($cards, currentCardIndex),
    });
  }

  // Enable/Disable buttons on card navigator
  if (isFirstCard(currentCardIndex)) {
    disableButton($cardNavigatorUpBtn);
  } else {
    enableButton($cardNavigatorUpBtn);
  }
  if (isLastCard($cards, currentCardIndex) || currentCardIndex < 0) {
    disableButton($cardNavigatorDownBtn);
  } else {
    enableButton($cardNavigatorDownBtn);
  }
};

const handleClickOnIndexBtn = ({ $cardNavigator, $cards, $overlay }) => {
  if (isCardNavigatorOpen($cardNavigator)) {
    hideCardNavigatorLinks($cardNavigator, $overlay);
  } else {
    showCardNavigatorLinks($cardNavigator, $overlay);
  }
  $cards.forEach(($card) => {
    openCard($card);
  });
};

const handleClickUp = ($cards) => {
  scrollToElement({ $element: $cards[currentCardIndex - 1], responsiveOffset: RESPONSIVE_OFFSET });
};

const handleClickDown = ($cards) => {
  scrollToElement({ $element: $cards[currentCardIndex + 1], responsiveOffset: RESPONSIVE_OFFSET });
};

const handleClickOnLink = ({ event, $cards, $overlay, cardId, $cardNavigator }) => {
  event.preventDefault();
  const $targetElement = filter(($card) => $card.id === cardId)($cards)[0];
  hideCardNavigatorLinks($cardNavigator, $overlay);
  scrollToElement({ $element: $targetElement, responsiveOffset: RESPONSIVE_OFFSET });
};

const handleCardIdFromUrl = ($cards, cardIdQueryParam) => {
  const $targetElement = filter(($card) => $card.id === cardIdQueryParam)($cards)[0];
  if (!$targetElement) {
    return;
  }
  openAllCards($cards);
  scrollToElement({ $element: $targetElement, responsiveOffset: RESPONSIVE_OFFSET, delay: SMOOTH_SCROLL_DELAY });
};

const handleRefreshInPlace = ({ $cards, $heroMediaWrapper, $cardNavigator, $playerHero }) => {
  openAllCards($cards);
  showCardNavigator($cardNavigator);
  toggleMediaWrapper({ $heroMediaWrapper, $playerHero, delay: SMOOTH_SCROLL_DELAY, show: false });
};

export default {
  openCard,
  openAllCards,
  getCardIdFromUrl,
  handleHideReadMoreContent,
  handleScrolling,
  handleClickOnIndexBtn,
  handleClickUp,
  handleClickDown,
  handleClickOnLink,
  handleCardIdFromUrl,
  handleRefreshInPlace,
};
